import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useMatch } from 'react-router-dom';
import { Menu, X, MessageSquare, Bell, Bot } from 'lucide-react';
import { SideNav } from './SideNav';
import { TopNav } from './TopNav';
import { FeedbackModal } from '../FeedbackModal';
import { NotificationsDropdown } from '../NotificationsDropdown';
import { TaskSidebar } from '../TaskSidebar';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isSideNavOpen, setIsSideNavOpen] = useState(true);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const isCalendarPage = useMatch('/calendar');

  const handleToggleSideNav = () => {
    setIsSideNavOpen(!isSideNavOpen);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 overflow-x-hidden w-full max-w-[100vw]">
      {/* Top Navigation */}
      <TopNav isSideNavOpen={isSideNavOpen} onMenuClick={handleToggleSideNav} />

      {/* Side Navigation - Desktop */}
      <div
        className={`fixed top-0 left-0 h-full bg-white dark:bg-dark-800 transform transition-transform duration-300 ease-in-out z-30 ${
          isSideNavOpen ? 'translate-x-0 w-64' : 'translate-x-0 w-20'
        } hidden lg:block shadow-md`}
      >
        <SideNav isCollapsed={!isSideNavOpen} onToggleCollapse={handleToggleSideNav} />
      </div>

      {/* Side Navigation - Mobile */}
      <AnimatePresence>
        {isSideNavOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40 lg:hidden"
              onClick={() => setIsSideNavOpen(false)}
            />
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 25 }}
              className="fixed top-0 left-0 h-full w-64 bg-white dark:bg-dark-800 z-50 lg:hidden shadow-xl"
            >
              <button
                onClick={() => setIsSideNavOpen(false)}
                className="absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 z-50"
                aria-label="Close menu"
                style={{ touchAction: 'manipulation' }}
              >
                <X className="h-6 w-6" />
              </button>
              <SideNav isCollapsed={false} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`transition-all duration-300 ease-in-out pt-16 overflow-x-hidden max-w-[100vw] ${
        isSideNavOpen ? 'lg:ml-64' : 'lg:ml-20'
      } ${isCalendarPage ? 'lg:mr-80' : ''}`}>
        <div className="p-3 sm:p-6 overflow-x-hidden max-w-full mobile-container">
          {children}
        </div>
      </main>

      {/* Task Sidebar */}
      {isCalendarPage && (
        <div className="fixed top-16 right-0 h-[calc(100vh-4rem)] w-full sm:w-80 max-w-full sm:max-w-xs hidden lg:block">
          <TaskSidebar />
        </div>
      )}

      {/* Fixed Button Container */}
      <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 flex flex-col items-center space-y-3 z-40">
        {/* Notification Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowNotifications(true)}
          className="relative p-2.5 sm:p-3 bg-white dark:bg-dark-800 text-indigo-600 dark:text-indigo-400 rounded-full shadow-lg hover:shadow-xl transition-all border border-indigo-100 dark:border-indigo-900/30 z-50"
        >
          <Bell className="h-5 w-5 sm:h-6 sm:w-6" />
          <span className="absolute top-1 right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
          </span>
        </motion.button>

        {/* Feedback Button */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowFeedback(true)}
          className="p-2.5 sm:p-3 relative z-50"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-indigo-400/20 dark:bg-indigo-600/20 rounded-full blur-xl" />
            <div className="relative p-3 sm:p-4 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full">
              <Bot className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
          </div>
        </motion.button>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
      />

      {/* Notifications Dropdown */}
      <NotificationsDropdown
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />
    </div>
  );
};